"use client";

import { useCallback, useEffect, useState } from "react";
import { useCheckPermissions } from "./useCheckPermissions";
import { useAccountActionPermissions } from "./useAccountActionPermissions";

/**
 * Returns what actions a user can perform on an Interface.
 *
 * Currently, all actions are allowed until the Permissions API can be used.
 */
export function useInterfaceActionPermissions(interfaceId: string) {
  // a toggleable state to opt in for fetching permissions
  const [permissionsCheckEnabled, setPermissionsCheckEnabled] = useState(false);
  const enablePermissionsCheck = useCallback(() => {
    setPermissionsCheckEnabled(true);
  }, []);

  const { permissions, checkPermissions, isLoading } = useCheckPermissions({
    assetId: interfaceId,
    assetType: "interface",
    permissionLevels: ["can_edit", "can_delete", "can_change_owner"],
  });

  const { allowTeams: hasTeams } = useAccountActionPermissions();

  // Only check permissions if the user has enabled the check
  // and the permissions have not been fetched yet
  useEffect(() => {
    if (interfaceId && permissionsCheckEnabled && !permissions && !isLoading) {
      checkPermissions();
    }
  }, [
    checkPermissions,
    interfaceId,
    isLoading,
    permissions,
    permissionsCheckEnabled,
  ]);

  return {
    canDelete: permissions?.canDelete || false,
    canDuplicate: permissions?.canEdit || false,
    canMove: permissions?.canEdit || false,
    canRename: permissions?.canEdit || false,
    canChangeOwner: (permissions?.canChangeOwner && hasTeams) || false,
    canShare: (permissions?.canEdit && hasTeams) || false,
    enablePermissionsCheck,
    isLoading,
    hasTeams,
  };
}
